import type { ObligationInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  Card,
  FormField,
  PageHeader,
  SectionHeader,
  SelectInput,
  StatePanel,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useApi } from "../api/ApiProvider";
import type { AccountRead, PurposeRead } from "../api/types";
import { QueryState } from "../components/QueryState";
import { formatMoney, parseMoneyInput } from "../lib/format";

type QuickBillFormValues = {
  name: string;
  expectedAmount: string;
  anchorDate: string;
  frequency: ObligationInput["recurrence"]["frequency"];
  kind: "bill" | "groceries";
  sourceAccountId: string;
};

type MoneyJobFormValues = {
  name: string;
  amount: string;
  kind: "emergency" | "custom";
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function editableMinor(minor: string): string {
  try {
    const value = BigInt(minor);
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    return `${negative ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

function positiveMinor(value: string): string | null {
  const parsed = parseMoneyInput(value);
  if (!parsed || parsed.startsWith("-") || parsed === "0") return null;
  return parsed;
}

function hasProtectedMoney(purpose: PurposeRead): boolean {
  try {
    return BigInt(purpose.protectedFloorMinor) > 0n;
  } catch {
    return false;
  }
}

function BalanceEditor({ account }: { account: AccountRead }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(() => editableMinor(account.currentBalanceMinor));
  const [formError, setFormError] = useState<string>();
  const mutation = useMutation({
    mutationFn: (currentBalanceMinor: string) =>
      api.updateAccount(account.id, { currentBalanceMinor }, account.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      await queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });

  const save = () => {
    setFormError(undefined);
    const currentBalanceMinor = parseMoneyInput(amount);
    if (!currentBalanceMinor) {
      setFormError("Enter the current balance in dollars and cents.");
      return;
    }
    mutation.mutate(currentBalanceMinor);
  };

  return (
    <Card>
      <div className="section-stack">
        <div className="list-row">
          <div className="list-row__main">
            <strong>{account.name}</strong>
            <span>
              {account.purposeName ?? "No job assigned"} · {account.type}
            </span>
          </div>
          <div className="list-row__side">
            <strong>{formatMoney(account.currentBalanceMinor, account.currency)}</strong>
          </div>
        </div>
        <div className="form-grid form-grid--two">
          <FormField label="Current balance" htmlFor={`quick-balance-${account.id}`}>
            <TextInput
              id={`quick-balance-${account.id}`}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.currentTarget.value)}
            />
          </FormField>
          <div className="button-row button-row--end">
            <Button type="button" onClick={save} isLoading={mutation.isPending}>
              Refresh balance
            </Button>
          </div>
        </div>
        {formError ? (
          <Callout title="Check this balance" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.isSuccess ? (
          <Callout title="Balance refreshed" tone="positive">
            IterNest Money is recalculating the household plan with this newer balance.
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Balance not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </div>
    </Card>
  );
}

export function QuickSetupPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [billError, setBillError] = useState<string>();
  const [jobError, setJobError] = useState<string>();

  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(),
  });
  const purposesQuery = useQuery({
    queryKey: ["purposes"],
    queryFn: () => api.getPurposes(),
  });
  const planQuery = useQuery({ queryKey: ["plan"], queryFn: () => api.getPlan() });

  const accounts = accountsQuery.data?.data ?? [];
  const purposes = purposesQuery.data?.data ?? [];
  const obligations = planQuery.data?.data.obligations ?? [];
  const moneyJobs = useMemo(() => purposes.filter(hasProtectedMoney), [purposes]);
  const assignedMinor = useMemo(
    () =>
      moneyJobs
        .reduce((total, purpose) => {
          try {
            return total + BigInt(purpose.protectedFloorMinor);
          } catch {
            return total;
          }
        }, 0n)
        .toString(),
    [moneyJobs],
  );
  const currency = accounts[0]?.currency ?? "USD";

  const billForm = useForm<QuickBillFormValues>({
    defaultValues: {
      name: "",
      expectedAmount: "",
      anchorDate: today(),
      frequency: "monthly",
      kind: "bill",
      sourceAccountId: "",
    },
  });

  const jobForm = useForm<MoneyJobFormValues>({
    defaultValues: { name: "", amount: "", kind: "custom" },
  });

  const billMutation = useMutation({
    mutationFn: async (values: QuickBillFormValues & { expectedAmountMinor: string }) => {
      const purposeType = values.kind === "groceries" ? "groceries_free_money" : "bills";
      let purpose = purposes.find((item) => item.type === purposeType);
      if (!purpose) {
        const created = await api.createPurpose({
          name: values.kind === "groceries" ? "Groceries" : "Bills",
          type: purposeType,
          priority: values.kind === "groceries" ? 20 : 10,
          protectionLevel: "standard",
          protectedFloorMinor: "0",
        });
        purpose = created.data;
      }
      return api.createObligation({
        name: values.name.trim(),
        type: values.kind,
        recurrence: {
          frequency: values.frequency,
          interval: 1,
          anchorDate: values.anchorDate,
        },
        amountMethod: "user_confirmed",
        expectedAmountMinor: values.expectedAmountMinor,
        purposeId: purpose.id,
        priority: values.kind === "groceries" ? 20 : 50,
        flexibility: "fixed",
        ...(values.sourceAccountId ? { sourceAccountId: values.sourceAccountId } : {}),
        splitAllowed: true,
        confidence: "confirmed",
      });
    },
    onSuccess: async () => {
      billForm.reset({
        name: "",
        expectedAmount: "",
        anchorDate: today(),
        frequency: "monthly",
        kind: "bill",
        sourceAccountId: "",
      });
      await queryClient.invalidateQueries({ queryKey: ["plan"] });
      await queryClient.invalidateQueries({ queryKey: ["purposes"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
    },
  });

  const jobMutation = useMutation({
    mutationFn: (values: MoneyJobFormValues & { amountMinor: string }) =>
      api.createPurpose({
        name: values.name.trim(),
        type: values.kind === "emergency" ? "emergency" : "custom",
        priority: values.kind === "emergency" ? 5 : 30,
        protectionLevel: "protected",
        protectedFloorMinor: values.amountMinor,
      }),
    onSuccess: async () => {
      jobForm.reset({ name: "", amount: "", kind: "custom" });
      await queryClient.invalidateQueries({ queryKey: ["purposes"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      await queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });

  const submitBill = billForm.handleSubmit((values) => {
    setBillError(undefined);
    const expectedAmountMinor = positiveMinor(values.expectedAmount);
    if (!values.name.trim() || !expectedAmountMinor || !values.anchorDate) {
      setBillError("Add the bill name, positive amount, and next due date.");
      return;
    }
    billMutation.mutate({ ...values, expectedAmountMinor });
  });

  const submitJob = jobForm.handleSubmit((values) => {
    setJobError(undefined);
    const amountMinor = positiveMinor(values.amount);
    if (!values.name.trim() || !amountMinor) {
      setJobError("Name this money and enter the amount that is already spoken for.");
      return;
    }
    jobMutation.mutate({ ...values, amountMinor });
  });

  const isPending = accountsQuery.isPending || purposesQuery.isPending || planQuery.isPending;
  const firstError = accountsQuery.error ?? purposesQuery.error ?? planQuery.error;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Start with what you know"
        title="Finish your money setup"
        description="You do not need every financial detail before IterNest Money can begin protecting the money you already understand."
      />

      <Callout title="This setup can be completed in pieces" tone="info">
        Add the bills you know today, give savings money a name, and refresh balances when they are
        available. You can return at any time.
      </Callout>

      <QueryState
        isPending={isPending}
        isError={Boolean(firstError)}
        error={firstError}
        onRetry={() => {
          void accountsQuery.refetch();
          void purposesQuery.refetch();
          void planQuery.refetch();
        }}
        loadingTitle="Opening your setup workspace"
      >
        <div className="summary-grid">
          <div className="summary-tile">
            <span>Accounts entered</span>
            <strong>{accounts.length}</strong>
          </div>
          <div className="summary-tile">
            <span>Bills and commitments</span>
            <strong>{obligations.length}</strong>
          </div>
          <div className="summary-tile">
            <span>Money already given a job</span>
            <strong>{formatMoney(assignedMinor, currency)}</strong>
          </div>
        </div>

        <Card>
          <SectionHeader
            title="Add a bill quickly"
            description="The everyday path asks only for the information most people already know. Payment account and advanced funding details can wait."
          />
          <form className="form-stack" onSubmit={submitBill}>
            <div className="form-grid form-grid--two">
              <FormField label="Bill name" htmlFor="quick-bill-name">
                <TextInput
                  id="quick-bill-name"
                  placeholder="Mortgage, phone, hockey tuition…"
                  {...billForm.register("name", { required: true })}
                />
              </FormField>
              <FormField label={`Amount (${currency})`} htmlFor="quick-bill-amount">
                <TextInput
                  id="quick-bill-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...billForm.register("expectedAmount", { required: true })}
                />
              </FormField>
              <FormField label="How often?" htmlFor="quick-bill-frequency">
                <SelectInput id="quick-bill-frequency" {...billForm.register("frequency")}>
                  <option value="once">Once</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every two weeks</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </SelectInput>
              </FormField>
              <FormField label="Next due date" htmlFor="quick-bill-date">
                <TextInput
                  id="quick-bill-date"
                  type="date"
                  {...billForm.register("anchorDate", { required: true })}
                />
              </FormField>
              <FormField label="What kind of commitment?" htmlFor="quick-bill-kind">
                <SelectInput id="quick-bill-kind" {...billForm.register("kind")}>
                  <option value="bill">Bill or recurring commitment</option>
                  <option value="groceries">Grocery money</option>
                </SelectInput>
              </FormField>
              <FormField
                label="Paid from"
                htmlFor="quick-bill-account"
                hint="Optional. Choose later when you are not sure."
              >
                <SelectInput id="quick-bill-account" {...billForm.register("sourceAccountId")}>
                  <option value="">Choose later</option>
                  {accounts
                    .filter((account) => account.type === "checking" || account.type === "savings")
                    .map((account) => (
                      <option value={account.id} key={account.id}>
                        {account.name}
                      </option>
                    ))}
                </SelectInput>
              </FormField>
            </div>
            <div className="button-row">
              <Button type="submit" isLoading={billMutation.isPending}>
                Save and add another
              </Button>
            </div>
            {billError ? (
              <Callout title="This bill needs one more detail" tone="warning">
                {billError}
              </Callout>
            ) : null}
            {billMutation.isSuccess ? (
              <Callout title="Bill added" tone="positive">
                Add another, or move on and return later.
              </Callout>
            ) : null}
            {billMutation.error ? (
              <Callout title="Bill not saved" tone="urgent">
                {billMutation.error.message}
              </Callout>
            ) : null}
          </form>
        </Card>

        <Card>
          <SectionHeader
            title="Give savings money a job"
            description="Use one job for each amount inside savings: emergency, annual bills, a trip, tuition, Christmas, or anything else."
          />
          <Callout title="This does not move your money" tone="info">
            A money job tells IterNest Money that an amount is already spoken for. It can represent
            several buckets inside one real savings account.
          </Callout>
          <form className="form-stack" onSubmit={submitJob}>
            <div className="form-grid form-grid--two">
              <FormField label="What is this money waiting for?" htmlFor="money-job-name">
                <TextInput
                  id="money-job-name"
                  placeholder="Emergency, annual bills, TPH…"
                  {...jobForm.register("name", { required: true })}
                />
              </FormField>
              <FormField
                label={`Amount already set aside (${currency})`}
                htmlFor="money-job-amount"
              >
                <TextInput
                  id="money-job-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...jobForm.register("amount", { required: true })}
                />
              </FormField>
              <FormField label="Job type" htmlFor="money-job-kind">
                <SelectInput id="money-job-kind" {...jobForm.register("kind")}>
                  <option value="custom">Named savings job</option>
                  <option value="emergency">Emergency reserve</option>
                </SelectInput>
              </FormField>
            </div>
            <div className="button-row">
              <Button type="submit" isLoading={jobMutation.isPending}>
                Protect this money
              </Button>
            </div>
            {jobError ? (
              <Callout title="This money needs a name and amount" tone="warning">
                {jobError}
              </Callout>
            ) : null}
            {jobMutation.isSuccess ? (
              <Callout title="Money job protected" tone="positive">
                IterNest Money will keep this amount out of ordinary safe-to-use money.
              </Callout>
            ) : null}
            {jobMutation.error ? (
              <Callout title="Money job not saved" tone="urgent">
                {jobMutation.error.message}
              </Callout>
            ) : null}
          </form>

          {moneyJobs.length === 0 ? (
            <StatePanel title="No savings money has a job yet">
              Add the first amount above. You can create several jobs even when the money lives in
              one savings account.
            </StatePanel>
          ) : (
            <div className="card-list">
              {moneyJobs.map((purpose) => (
                <div className="list-row" key={purpose.id}>
                  <div className="list-row__main">
                    <strong>{purpose.name}</strong>
                    <span>
                      {purpose.type === "emergency" ? "Emergency reserve" : "Protected money job"}
                    </span>
                  </div>
                  <div className="list-row__side">
                    <strong>{formatMoney(purpose.protectedFloorMinor, currency)}</strong>
                    <span>spoken for</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <section className="section-stack" aria-labelledby="refresh-balances-title">
          <SectionHeader
            title="Refresh account balances"
            description="Manual balances become uncertain over time. Updating one here gives IterNest Money a new trustworthy starting point."
          />
          <h2 className="sr-only" id="refresh-balances-title">
            Refresh account balances
          </h2>
          {accounts.length === 0 ? (
            <StatePanel title="No accounts are entered yet">
              Add a manual account under Accounts & imports, then return here to keep its balance
              current.
            </StatePanel>
          ) : (
            <div className="card-list">
              {accounts.map((account) => (
                <BalanceEditor account={account} key={`${account.id}-${account.version}`} />
              ))}
            </div>
          )}
        </section>
      </QueryState>
    </div>
  );
}
